import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { fetchCard, fetchCardLinks, fetchPublicUser, recordCardVisit, recordLinkClick } from "../utils/api";
import Skeleton, { CardSkeleton } from "./Skeleton";
import CardRenderer, { CardRendererCard, CardRendererLink } from "./CardRenderer";

/**
 * Specific-card public view, addressable by card id (D-001).
 *
 * Post-BL-008 a handle URL (`/card/{handle}`) only ever resolves to an
 * account's ACTIVE card. This view renders one EXACT card by its id — regardless
 * of active status — reusing the same already-public display data
 * (`fetchCard(id)` -> `GET /api/cards/:id`, the same endpoint PublicCardForm
 * uses) and the shared CardRenderer.
 *
 * Scope (D-001): this exists for the curated homepage PreviewCard demo. It is
 * NOT a general per-card-URL product surface, so the page is marked noindex and
 * the route is kept out of the sitemap/footer. It exposes only public card
 * display fields (name/position/company/theme/logos/links) — never
 * account/auth/PII — because it renders exactly what `mapCard` already returns
 * for every public card. Unknown/invalid ids render the shared 404 state.
 */
const PublicCardById: React.FC = () => {
    const { isSignedIn } = useUser();
    const { cardId } = useParams<{ cardId: string }>();
    const [card, setCard] = useState<CardRendererCard | null>(null);
    const [bio, setBio] = useState<string>('');
    const [address, setAddress] = useState<string>('');
    const [links, setLinks] = useState<CardRendererLink[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isPro, setIsPro] = useState(false);
    const [resolvedTheme, setResolvedTheme] = useState('default');

    const searchParams = new URLSearchParams(window.location.search);
    const themeParam = searchParams.get('theme');

    useEffect(() => {
        const loadCard = async () => {
            if (!cardId) {
                setError("Invalid card URL");
                setLoading(false);
                return;
            }

            try {
                // Resolve the EXACT card by id, independent of which card the
                // account has active. This is the same public endpoint
                // PublicCardForm reads (fetchCard -> GET /api/cards/:id).
                const cardData = await fetchCard(cardId);
                setCard(cardData);
                setResolvedTheme(themeParam || cardData.theme || 'default');

                try {
                    // Public-safe projection scoped to this card so the server
                    // returns the address only if this card opts to show it.
                    const userData = await fetchPublicUser(cardData.userId, cardData.id);
                    if (userData.bio) setBio(userData.bio);
                    if (userData.shippingAddress) setAddress(userData.shippingAddress);
                    setIsPro(userData.isPro);
                } catch {}

                const refParam = new URLSearchParams(window.location.search).get('ref');
                recordCardVisit(cardData.id, refParam || undefined).catch(() => {});

                const cardLinks = await fetchCardLinks(cardData.id);
                setLinks(cardLinks.sort((a, b) => a.order - b.order) as CardRendererLink[]);
            } catch (err) {
                setError("Card not found");
            } finally {
                setLoading(false);
            }
        };

        loadCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardId]);

    // Keep specific-card URLs out of search results — this view is a curated
    // demo surface, not a general public directory (D-001).
    useEffect(() => {
        const meta = document.createElement('meta');
        meta.setAttribute('name', 'robots');
        meta.setAttribute('content', 'noindex');
        document.head.appendChild(meta);
        return () => {
            document.head.removeChild(meta);
        };
    }, []);

    useEffect(() => {
        if (!card) return;

        const originalTitle = document.title;
        document.title = `${card.name} - WhoBrew`;
        return () => {
            document.title = originalTitle;
        };
    }, [card]);

    const handleLinkClick = (link: CardRendererLink) => {
        recordLinkClick(link.id).catch(() => {});
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-cream-50 dark:bg-coffee-900">
                <div className="max-w-md mx-auto px-4 py-8">
                    <div className="h-20 rounded-t-lg bg-cream-100 dark:bg-coffee-800 animate-pulse mb-0" />
                    <div className="text-center -mt-9 mb-6 px-4">
                        <Skeleton variant="circular" width="72px" height="72px" className="mx-auto mb-3" />
                        <Skeleton width="160px" height="22px" className="mx-auto mb-2" />
                        <Skeleton width="120px" height="16px" className="mx-auto mb-1" />
                        <Skeleton width="140px" height="12px" className="mx-auto" />
                    </div>
                    <CardSkeleton />
                </div>
            </div>
        );
    }

    if (error || !card) {
        return (
            <div className="min-h-screen bg-cream-50 dark:bg-coffee-900 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto px-6 py-20">
                    <p className="font-mono text-eyebrow uppercase text-coffee-600 dark:text-cream-200 mb-4">404</p>
                    <h1 className="font-display text-4xl sm:text-display-md text-coffee-900 dark:text-cream-50 tracking-tight">
                        Card not found.
                    </h1>
                    <p className="mt-6 text-body text-coffee-700 dark:text-cream-200 max-w-sm mx-auto">
                        This card doesn't exist, or it's been removed by its owner.
                    </p>
                    <div className="mt-10 flex flex-wrap justify-center gap-3">
                        <a
                            href="/"
                            className="inline-flex items-center justify-center h-12 px-6 rounded bg-coffee-900 text-cream-50 text-sm font-medium hover:bg-coffee-800 transition-colors"
                        >
                            Back to WhoBrew
                        </a>
                        <a
                            href="/contact"
                            className="inline-flex items-center justify-center h-12 px-6 rounded border border-coffee-900 dark:border-cream-50 text-coffee-900 dark:text-cream-50 text-sm font-medium hover:bg-coffee-900 hover:text-cream-50 transition-colors"
                        >
                            Contact us
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <CardRenderer
            card={card}
            links={links}
            bio={bio}
            address={address}
            isPro={isPro}
            resolvedTheme={resolvedTheme}
            isSignedIn={!!isSignedIn}
            onLinkClick={handleLinkClick}
        />
    );
};

export default PublicCardById;
